import { FC } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { grey } from '@mui/material/colors';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Column, Row } from 'further-ui/components/Layout';
import { type Column as TableColumn } from 'further-ui/components/Table';
import { sumBy } from 'lodash';
import { AssetTypeLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';
import { AccountingReportDateOption } from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import FullHeightView from 'components/new/FullHeightView';
import PageFilters from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';
import DatePresetFilter, {
  accountingDateLabels,
} from '../components/DatePresetFilter';
import { formatMultiple, formatIRR, formatPct } from 'further-ui/utils';

type UnrealisedRow = {
  id: string;
  assetName: string;
  assetType: string;
  sharesHeld: number;
  fundOwnershipPct: number | null;
  initialInvestmentDate: CalendarDay;
  acquisitionCostLcy: number;
  fairValueAdjustmentLcy: number;
  fxAdjustmentLcy: number;
  investmentFairValueLcy: number;
  grossMOIC: number | null;
  grossImpliedIRR: number;
};

type RealisedRow = {
  id: string;
  assetName: string;
  assetType: string;
  sharesRealised: number;
  initialInvestmentDate: CalendarDay;
  acquisitionCostLcy: number;
  realisationDate: CalendarDay;
  cashReceivedLcy: number;
  cashEscrowedLcy: number;
  potentialEarnoutLcy: number;
  shareConsiderationLcy: number;
  totalConsiderationLcy: number;
  grossMOIC: number | null;
  grossIRR: number;
};

const pageId = 'schedule-of-investments';

const ValueColumn = styled(Column)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.sm),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: 0,
  },
}));

const Total = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  paddingTop: theme.spacing(theme.spacingSizes.xxs),
  paddingBottom: theme.spacing(theme.spacingSizes.xxs),
  paddingLeft: theme.spacing(theme.spacingSizes.xs),
}));

const ScheduleOfInvestments: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: AccountingReportDateOption;
    customDate?: CalendarDay;
  }>(pageId, {
    defaultFilters: {
      dateOption: AccountingReportDateOption.EndLastQuarter,
      customDate: undefined,
    },
  });
  const { dateOption, customDate } = filters;
  const isCustomDate = dateOption === AccountingReportDateOption.Custom;
  const isCustomDateUnset = isCustomDate && !customDate;

  const {
    data: scheduleData,
    isLoading,
    isFetching,
  } = api.generalLedger.scheduleOfInvestments.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    },
    {
      enabled: showOtherAccounting && !!selectedVehicleId && !isCustomDateUnset,
    },
  );

  const exportSchedule =
    api.generalLedger.exportScheduleOfInvestments.useMutation(
      useHandleTrpcMutation<{ file: string; fileName: string }>()({
        refetchType: 'none',
        onSuccess: ({ file, fileName }) => {
          downloadXlxs(file, fileName);
        },
      }),
    );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (isCustomDateUnset) return;
    exportSchedule.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const { baseCurrency } = vehicle;

  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  const unrealisedColumns: Array<TableColumn<UnrealisedRow>> = [
    {
      key: 'assetName',
      label: 'Asset name',
      width: 200,
      textOverflowEllipsis: true,
    },
    {
      key: 'assetType',
      label: 'Asset type',
      width: 160,
      noWrap: true,
      render: ({ assetType }) => AssetTypeLabels[assetType] ?? assetType,
    },
    {
      key: 'sharesHeld',
      label: 'Shares held',
      width: 160,
      noWrap: true,
      render: ({ sharesHeld }) => sharesHeld.toLocaleString('en-GB'),
    },
    {
      key: 'fundOwnershipPct',
      label: 'Fund ownership % (fully diluted)',
      width: 210,
      noWrap: true,
      withVerticalBorder: true,
      render: ({ fundOwnershipPct }) => formatPct(fundOwnershipPct),
    },
    {
      key: 'initialInvestmentDate',
      label: 'Initial investment date',
      width: 160,
      noWrap: true,
      render: ({ initialInvestmentDate }) => initialInvestmentDate.toReadable(),
    },
    {
      key: 'acquisitionCostLcy',
      label: `Acquisition cost in ${baseCurrency}`,
      width: 180,
      noWrap: true,
      render: ({ acquisitionCostLcy }) => formatValue(acquisitionCostLcy),
    },
    {
      key: 'fairValueAdjustmentLcy',
      label: `Fair value adjustment in ${baseCurrency}`,
      width: 200,
      noWrap: true,
      render: ({ fairValueAdjustmentLcy }) =>
        formatValue(fairValueAdjustmentLcy),
    },
    {
      key: 'fxAdjustmentLcy',
      label: `FX adjustment in ${baseCurrency}`,
      width: 180,
      noWrap: true,
      render: ({ fxAdjustmentLcy }) => formatValue(fxAdjustmentLcy),
    },
    {
      key: 'investmentFairValueLcy',
      label: `Investment fair value in ${baseCurrency}`,
      width: 180,
      noWrap: true,
      render: ({ investmentFairValueLcy }) =>
        formatValue(investmentFairValueLcy),
    },
    {
      key: 'grossMOIC',
      label: 'Gross MOIC',
      width: 120,
      noWrap: true,
      render: ({ grossMOIC }) => formatMultiple(grossMOIC),
    },
    {
      key: 'grossImpliedIRR',
      label: 'Gross implied IRR',
      width: 140,
      noWrap: true,
      render: ({ grossImpliedIRR }) => formatIRR(grossImpliedIRR),
    },
  ];

  const realisedColumns: Array<TableColumn<RealisedRow>> = [
    {
      key: 'assetName',
      label: 'Asset name',
      width: 200,
      textOverflowEllipsis: true,
    },
    {
      key: 'assetType',
      label: 'Asset type',
      width: 160,
      noWrap: true,
      render: ({ assetType }) => AssetTypeLabels[assetType] ?? assetType,
    },
    {
      key: 'sharesRealised',
      label: 'Shares realised',
      width: 160,
      noWrap: true,
      withVerticalBorder: true,
      render: ({ sharesRealised }) => sharesRealised.toLocaleString('en-GB'),
    },
    {
      key: 'initialInvestmentDate',
      label: 'Initial investment date',
      width: 160,
      noWrap: true,
      render: ({ initialInvestmentDate }) => initialInvestmentDate.toReadable(),
    },
    {
      key: 'acquisitionCostLcy',
      label: `Acquisition cost in ${baseCurrency}`,
      width: 180,
      noWrap: true,
      render: ({ acquisitionCostLcy }) => formatValue(acquisitionCostLcy),
    },
    {
      key: 'realisationDate',
      label: 'Realisation date',
      width: 160,
      noWrap: true,
      render: ({ realisationDate }) => realisationDate.toReadable(),
    },
    {
      key: 'cashReceivedLcy',
      label: `Cash received in ${baseCurrency}`,
      width: 180,
      noWrap: true,
      render: ({ cashReceivedLcy }) => formatValue(cashReceivedLcy),
    },
    {
      key: 'cashEscrowedLcy',
      label: 'Cash escrowed at reporting date',
      width: 200,
      noWrap: true,
      render: ({ cashEscrowedLcy }) => formatValue(cashEscrowedLcy),
    },
    {
      key: 'potentialEarnoutLcy',
      label: 'Potential earnout at reporting date',
      width: 210,
      noWrap: true,
      render: ({ potentialEarnoutLcy }) => formatValue(potentialEarnoutLcy),
    },
    {
      key: 'shareConsiderationLcy',
      label: `Share consideration in ${baseCurrency}`,
      width: 190,
      noWrap: true,
      render: ({ shareConsiderationLcy }) => formatValue(shareConsiderationLcy),
    },
    {
      key: 'totalConsiderationLcy',
      label: `Total consideration in ${baseCurrency}`,
      width: 190,
      noWrap: true,
      render: ({ totalConsiderationLcy }) => formatValue(totalConsiderationLcy),
    },
    {
      key: 'grossMOIC',
      label: 'Gross MOIC (cash and shares)',
      width: 190,
      noWrap: true,
      render: ({ grossMOIC }) => formatMultiple(grossMOIC),
    },
    {
      key: 'grossIRR',
      label: 'Gross IRR (received cash only)',
      width: 190,
      noWrap: true,
      render: ({ grossIRR }) => formatIRR(grossIRR),
    },
  ];

  const unrealisedRows: Array<UnrealisedRow> =
    scheduleData?.unrealisedRows.map((r) => ({ ...r, id: r.assetId })) ?? [];

  const realisedRows: Array<RealisedRow> =
    scheduleData?.realisedRows.map((r) => ({ ...r, id: r.realisationId })) ??
    [];

  const totalUnrealisedAcqCost = sumBy(unrealisedRows, 'acquisitionCostLcy');
  const totalUnrealisedFairValueAdj = sumBy(
    unrealisedRows,
    'fairValueAdjustmentLcy',
  );
  const totalUnrealisedFxAdj = sumBy(unrealisedRows, 'fxAdjustmentLcy');
  const totalUnrealisedFairValue = sumBy(
    unrealisedRows,
    'investmentFairValueLcy',
  );
  const totalUnrealisedMOIC =
    totalUnrealisedAcqCost !== 0
      ? totalUnrealisedFairValue / totalUnrealisedAcqCost
      : null;

  const totalRealisedAcqCost = sumBy(realisedRows, 'acquisitionCostLcy');
  const totalCashReceived = sumBy(realisedRows, 'cashReceivedLcy');
  const totalCashEscrowed = sumBy(realisedRows, 'cashEscrowedLcy');
  const totalEarnout = sumBy(realisedRows, 'potentialEarnoutLcy');
  const totalShareConsideration = sumBy(realisedRows, 'shareConsiderationLcy');
  const totalConsideration = sumBy(realisedRows, 'totalConsiderationLcy');
  const totalRealisedMOIC =
    totalRealisedAcqCost !== 0
      ? totalConsideration / totalRealisedAcqCost
      : null;

  return (
    <SyncScrollProvider>
      <FullHeightView>
        <Column spacing="lg" fullHeight>
          <Row centered spacing="sm" justify="space-between" breakpoint="md">
            <Column spacing="xs">
              <Typography variant="h5" color="grey.900" noWrap>
                Schedule of investments
              </Typography>
              <Typography variant="body2" noWrap={!isTablet}>
                {vehicle.vehicleName} as at{' '}
                {isCustomDate
                  ? isCustomDateUnset
                    ? '...'
                    : customDate?.toReadable()
                  : accountingDateLabels[dateOption].toLowerCase()}
                {isCustomDate ? (isCustomDateUnset ? '' : '.') : '.'}
              </Typography>
            </Column>
            <PageFilters
              justify="flex-end"
              align="center"
              actions={[
                {
                  label: 'Export',
                  variant: 'contained',
                  onClick: handleExport,
                  disabled: isCustomDateUnset,
                },
              ]}
            >
              <DatePresetFilter
                dateOption={dateOption}
                customDate={customDate}
                onChange={(dateOption, customDate) =>
                  handleChangeFilters({ dateOption, customDate })
                }
              />
            </PageFilters>
          </Row>
          <Divider />
          <Row spacing={isMobile ? 'lg' : 'xl'} breakpoint="sm">
            <ValueColumn spacing="xs">
              <Typography variant="body2" color="grey.600">
                Total portfolio acquisition value
              </Typography>
              <Typography
                variant="h3"
                color={isFetching ? 'grey.600' : 'grey.900'}
              >
                {isFetching
                  ? '--'
                  : formatValue(
                      scheduleData?.totalPortfolioAcquisitionValue ?? 0,
                    )}
              </Typography>
            </ValueColumn>
            <Divider
              orientation={isMobile ? 'horizontal' : 'vertical'}
              flexItem
            />
            <Column spacing="xs">
              <Typography variant="body2" color="grey.600">
                Total portfolio current value
              </Typography>
              <Typography
                variant="h3"
                color={isFetching ? 'grey.600' : 'grey.900'}
              >
                {isFetching
                  ? '--'
                  : formatValue(scheduleData?.totalPortfolioCurrentValue ?? 0)}
              </Typography>
            </Column>
          </Row>
          <Column spacing="lg">
            <AccountingSection
              title="Unrealised holdings"
              titleVariant="h6"
              expandable={false}
            >
              <AccountingTable
                columns={unrealisedColumns}
                rows={unrealisedRows}
                isLoading={isFetching}
                emptyMessage="No unrealised holdings."
                bodyRowPaddingY={unrealisedRows.length === 0 ? 30 : undefined}
                borderless
                externalScroll={false}
                customFooterRow={[
                  {
                    key: 'totals',
                    backgroundColor: grey[100],
                    colspan: 5,
                    content: <Total variant="h6">Totals</Total>,
                  },
                  {
                    key: 'acquisitionCostLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalUnrealisedAcqCost)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'fairValueAdjustmentLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalUnrealisedFairValueAdj)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'fxAdjustmentLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalUnrealisedFxAdj)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'investmentFairValueLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalUnrealisedFairValue)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'grossMOIC',
                    backgroundColor: grey[100],
                    colspan: 2,
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatMultiple(totalUnrealisedMOIC)}
                      </Typography>
                    ),
                  },
                ]}
              />
            </AccountingSection>
            <AccountingSection
              title="Realised holdings"
              titleVariant="h6"
              expandable={false}
            >
              <AccountingTable
                columns={realisedColumns}
                rows={realisedRows}
                isLoading={isFetching}
                emptyMessage="No realised holdings."
                bodyRowPaddingY={realisedRows.length === 0 ? 30 : undefined}
                borderless
                externalScroll={false}
                customFooterRow={[
                  {
                    key: 'totals',
                    backgroundColor: grey[100],
                    colspan: 4,
                    content: <Total variant="h6">Totals</Total>,
                  },
                  {
                    key: 'acquisitionCostLcy',
                    backgroundColor: grey[100],
                    colspan: 2,
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalRealisedAcqCost)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'cashReceivedLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalCashReceived)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'cashEscrowedLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalCashEscrowed)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'potentialEarnoutLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalEarnout)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'shareConsiderationLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalShareConsideration)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'totalConsiderationLcy',
                    backgroundColor: grey[100],
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(totalConsideration)}
                      </Typography>
                    ),
                  },
                  {
                    key: 'grossMOIC',
                    backgroundColor: grey[100],
                    colspan: 2,
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatMultiple(totalRealisedMOIC)}
                      </Typography>
                    ),
                  },
                ]}
              />
            </AccountingSection>
          </Column>
        </Column>
      </FullHeightView>
    </SyncScrollProvider>
  );
};

export default ScheduleOfInvestments;
